/**
 * Kendi API'mize giden isteklere oturum başlığını merkezî olarak ekler.
 *
 * Neden: backend uçlarının çoğu eskiden kimlik doğrulaması İSTEMİYORDU
 * (`/atm/query`, `/list/execute/...`, `/form/execute-sql` gibi), bu yüzden
 * çağrı yerleri `Authorization` başlığı göndermeden yazılmıştı. Uçlar
 * kapatılırken 100'ü aşkın çağrı yerini tek tek düzeltmek yerine başlık burada
 * ekleniyor.
 *
 * HEM axios HEM de `fetch` sarılır: kod tabanında 103 ayrı `fetch()` çağrısı
 * var ve yalnızca axios sarılırsa o çağrılar 401 alır.
 *
 * Başlık YALNIZCA kendi API'mize eklenir — Google Maps, imgproxy
 * (dosya.atmturkiye.com) gibi dış servislere token sızmamalı. Çağrı yerinde
 * zaten bir Authorization başlığı varsa ona dokunulmaz.
 *
 * 03.11.2026: yanıtlar da burada okunur — X-Oturum-Kalan / X-Oturum-Tur
 * ekrandaki oturum sayacını besler (utils/oturumSaati.js); oturum sunucuda
 * bittiyse (401 + WWW-Authenticate) kullanıcı mesajla giriş ekranına döner.
 * Böylece 103 çağrı yerinin hiçbirinde ayrıca 401 işlemek gerekmez.
 *
 * `index.js` tarafından uygulama açılışında bir kez kurulur.
 */
import axios from 'axios';
import { API_BASE_URL } from './config';
import { getToken } from './auth';
import oturumSaati from './oturumSaati';

/** URL bizim API'mize mi gidiyor? Göreli yollar da bize gider. */
const isOwnApi = (url) => {
  if (!url) return true; // baseURL üzerinden giden istek
  const s = String(url);
  if (/^https?:\/\//i.test(s)) return s.startsWith(API_BASE_URL);
  return true; // '/list/...' gibi göreli yollar
};

// Giriş adımları ve şifre sıfırlama kendi 401/429'unu ekranda gösterir; oturum bitişi sayılmaz.
const OTURUMSUZ = /\/(login\/(login|dogrula|kod-tekrar|altcha|cikis)|password-reset\/)/;

/** Yanıttaki oturum bilgisini işler. govdeOku yalnız 401'de çağrılır (büyük indirmeler kopyalanmasın). */
const oturumYaniti = (url, status, baslik, govdeOku) => {
  const kalan = baslik('x-oturum-kalan');
  if (kalan !== null && kalan !== undefined && kalan !== '') oturumSaati.guncelle(Number(kalan), baslik('x-oturum-tur'));
  if (status === 401 && baslik('www-authenticate') && !OTURUMSUZ.test(String(url || ''))) {
    Promise.resolve()
      .then(govdeOku)
      .then((g) => oturumSaati.bitir(g && typeof g.detail === 'string' ? g.detail : undefined))
      .catch(() => oturumSaati.bitir());
  }
};

const axiosBasligi = (yanit) => (h) => {
  const b = yanit && yanit.headers;
  if (!b) return undefined;
  return typeof b.get === 'function' ? b.get(h) : b[h];
};

const tokenliAxios = (config) => !!config && isOwnApi(config.url)
  && !!(config.headers && (config.headers.Authorization || config.headers.authorization));

let kuruldu = false;

export const setupApiAuth = () => {
  if (kuruldu) return; // HMR'da iki kez kurulmasın
  kuruldu = true;

  // --- axios ---------------------------------------------------------------
  axios.interceptors.request.use((config) => {
    if (!isOwnApi(config.url)) return config;
    const mevcut = config.headers?.Authorization || config.headers?.authorization;
    if (mevcut) return config;
    const token = getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (yanit) => {
      if (tokenliAxios(yanit.config)) {
        oturumYaniti(yanit.config.url, yanit.status, axiosBasligi(yanit), () => yanit.data);
      }
      return yanit;
    },
    (hata) => {
      const yanit = hata && hata.response;
      if (yanit && tokenliAxios(yanit.config)) {
        oturumYaniti(yanit.config.url, yanit.status, axiosBasligi(yanit), () => yanit.data);
      }
      return Promise.reject(hata);
    },
  );

  // --- fetch ---------------------------------------------------------------
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  const orijinalFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    let url;
    try {
      // input: string | URL | Request
      url = typeof input === 'string' ? input
        : (input && typeof input === 'object' && 'url' in input) ? input.url
        : String(input);
    } catch (e) {
      return orijinalFetch(input, init);
    }

    if (!isOwnApi(url)) return orijinalFetch(input, init);

    const token = getToken();
    if (!token) return orijinalFetch(input, init);

    const izle = (istek) => istek.then((yanit) => {
      // Kopya şimdi alınmalı: çağıran gövdeyi okuduktan sonra clone() hata verir.
      const kopya = yanit.status === 401 ? yanit.clone() : null;
      oturumYaniti(url, yanit.status, (h) => yanit.headers.get(h), () => kopya.json());
      return yanit;
    });

    // Request nesnesi verilmişse başlığı onun üzerinden ekle; string/URL ise init'te.
    if (input && typeof input === 'object' && 'url' in input && typeof Request !== 'undefined'
        && input instanceof Request) {
      if (input.headers.get('Authorization')) return izle(orijinalFetch(input, init));
      const yeni = new Request(input, {
        headers: (() => {
          const h = new Headers(input.headers);
          h.set('Authorization', `Bearer ${token}`);
          return h;
        })(),
      });
      return izle(orijinalFetch(yeni, init));
    }

    const ayar = { ...(init || {}) };
    const h = new Headers(ayar.headers || {});
    if (!h.get('Authorization')) h.set('Authorization', `Bearer ${token}`);
    ayar.headers = h;
    return izle(orijinalFetch(input, ayar));
  };
};

export default setupApiAuth;
